import React, { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SnippetsEnabledTextField from "../SnippetsEnabledTextField";

import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Select,
  Typography,
} from "@mui/material";

import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import { blueGrey, green, red } from "@mui/material/colors";
import { PatientContext } from "../../contexts/PatientContext.tsx";
import { useFetch } from "../../services/hooks/useFetch";
import { projectsPrefix } from "../../services/ProjectsServices";

interface RelatedVisitModalProps {
  anchorEl: Parameters<typeof Popover>[0]["anchorEl"];
  onClose: () => void;
  visit: EncounterSearchResult;
  handleVisitUpdate: (updatedVisit: EncounterSearchResult) => void;
}

export default function RelatedVisitModal({
  anchorEl,
  onClose,
  visit,
  handleVisitUpdate,
}: RelatedVisitModalProps) {
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId");
  const { id } = useContext(PatientContext)!;
  const [isRelated, setIsRelated] = useState<boolean | null>(visit.isRelated);
  const {
    update: updateVisitWithReason,
    isLoading,
    data,
  } = useFetch<EncounterSearchResult, EncounterRelatedRequest>();

  function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    const payload = {
      isRelated: isRelated,
      reason: note,
    };
    updateVisitWithReason({
      path: `${projectsPrefix}/${projectId}/patients/${id}/encounters/${visit.id}/related`,
      body: payload,
    });
  }

  useEffect(() => {
    if (data && !isLoading && !!data.id) {
      const updatedVisit = {
        ...visit,
        isRelated: data.isRelated,
        reason: data.reason,
      };
      handleVisitUpdate(updatedVisit);
      setLoading(false);
      onClose();
    }
  }, [visit, data, isLoading, handleVisitUpdate, onClose]);

  const { snippetsEnabledTextField } = SnippetsEnabledTextField({
    labelText: "Enter Note/Reason",
    textChanged: setNote,
    snippetType: "ENCOUNTER",
    attributeTypeId: null,
    snippetDialogWidthPx: 560,
    snippetDialogHeightPx: 320,
  });

  return (
    <Popover
      anchorEl={anchorEl}
      open={true}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <form onSubmit={onSubmit}>
        <Card className="mark-visit-modal">
          <CardHeader
            title={<Typography variant={"h5"}>Mark Visit As</Typography>}
            action={
              <IconButton
                onClick={onClose}
                sx={{ "&:hover": { bgcolor: "action.hover" } }}
              >
                <CloseIcon />
              </IconButton>
            }
            sx={{ width: "95%" }}
          />
          <CardContent className="mark-visit-modal-content">
            <FormControl size="small" className="mark-dropdown">
              <InputLabel>Mark Visit As</InputLabel>
              <Select
                label="Mark Visit As"
                value={String(isRelated)}
                onChange={(event) => {
                  const value = event.target.value;
                  setIsRelated(
                    value === "true" ? true : value === "false" ? false : null,
                  );
                }}
                variant="standard"
              >
                <MenuItem value={"true"}>
                  <Box className="related-modal-select-icon">
                    <ListItemIcon>
                      <ThumbUpOutlinedIcon
                        fontSize="small"
                        sx={{ color: green[600] }}
                      />
                    </ListItemIcon>
                    <ListItemText>Related</ListItemText>
                  </Box>
                </MenuItem>
                <MenuItem value={"false"}>
                  <Box className="related-modal-select-icon">
                    <ListItemIcon>
                      <BlockOutlinedIcon
                        fontSize="small"
                        sx={{ color: red[600] }}
                      />
                    </ListItemIcon>
                    <ListItemText>Unrelated</ListItemText>
                  </Box>
                </MenuItem>
                <MenuItem value={"null"}>
                  <Box className="related-modal-select-icon">
                    <ListItemIcon>
                      <PersonOutlinedIcon
                        fontSize="small"
                        sx={{ color: blueGrey[600] }}
                      />
                    </ListItemIcon>
                    <ListItemText>Undetermined</ListItemText>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            <div className="categorize-modal-textfield">
              {snippetsEnabledTextField}
            </div>
          </CardContent>
          <CardActions className={"categorize-modal-footer"}>
            <Button size={"small"} variant={"outlined"} onClick={onClose}>
              Cancel
            </Button>
            <Button
              size={"small"}
              type={"submit"}
              variant={"contained"}
              endIcon={loading && <CircularProgress size="1.5rem" />}
              disabled={loading}
            >
              Submit
            </Button>
          </CardActions>
        </Card>
      </form>
    </Popover>
  );
}
